import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";

/**
 * Global error overlay — surfaces silent JS crashes that would otherwise be
 * invisible to mobile users (iPad Safari users in particular have reported
 * the login button doing "nothing"; without this overlay we cannot tell
 * whether the click handler crashed, the API call failed, or the click
 * never reached React at all).
 *
 * Mounts at the top of the app tree, listens for the two browser-level
 * error channels (`error` and `unhandledrejection`) and renders a dismissible
 * red banner with the actual message + stack snippet. Also exposes a tiny
 * `window.__seavantoDebug.log(msg)` helper so we can append manual breadcrumbs
 * from suspicious code paths and read them back on the device.
 */
const MAX_LOGS = 20;
// The debug UI (pill + panel) is only shown to this account — error capture
// and breadcrumb logging keep running for everyone so crumbs can still be
// read from localStorage when diagnosing a user's device.
const DEBUG_ACCOUNT = "faruk@yerli.cc";

export default function GlobalErrorOverlay() {
  const { user } = useAuth();
  const [errors, setErrors] = useState([]);
  const [logs, setLogs] = useState([]);
  const [openLogs, setOpenLogs] = useState(false);

  useEffect(() => {
    const pushError = (msg, source) => {
      setErrors((prev) => [...prev, { msg: String(msg || "Unknown error"), source, at: Date.now() }]);
    };
    const onError = (e) => {
      try {
        const text = e?.error?.stack || e?.message || String(e);
        const filename = e?.filename || "";
        // Censored cross-origin script errors arrive as the literal string
        // "Script error." with no filename/lineno/stack. They originate from
        // third-party scripts (CDN, embedded widgets) and the browser hides
        // the details for security. We cannot read, fix, or act on them, and
        // popping the red banner every time one fires is just noise for the
        // user. Drop them silently — anything actionable will carry detail.
        if (text === "Script error." && !filename && !e?.error) return;
        pushError(text, "window.onerror" + (filename ? " · " + filename : ""));
      } catch { pushError("error event (unreadable)", "window.onerror"); }
    };
    const onRejection = (e) => {
      try {
        const r = e?.reason;
        // 1. AbortError is harmless — fires when a fetch is cancelled by
        //    the browser (e.g. user navigates away mid-request). Common on
        //    slower Android browsers when tapping menu items in quick
        //    succession. Don't pop the red banner for these.
        // 2. CanceledError is axios's equivalent (status code "ERR_CANCELED").
        const name = r?.name || "";
        const code = r?.code || "";
        if (name === "AbortError" || name === "CanceledError" || code === "ERR_CANCELED") {
          // Still log as a breadcrumb so we can see them when debugging.
          try { window.__seavantoDebug && window.__seavantoDebug.log(`promise cancelled (${name || code})`); } catch {}
          return;
        }
        // Build a more readable surface — the message and constructor name
        // are usually more informative than just the minified stack.
        const parts = [];
        if (name) parts.push(name);
        const msg = r?.message;
        if (msg) parts.push(msg);
        const stack = r?.stack ? "\n" + r.stack : (typeof r === "string" ? r : "");
        const text = (parts.join(": ") || JSON.stringify(r)) + stack;
        pushError(text, "unhandledrejection");
      } catch { pushError("rejection event (unreadable)", "unhandledrejection"); }
    };
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);

    // Expose a tiny debug helper for manual breadcrumbs from suspect code paths.
    // Persisted to localStorage so we can read crumbs even after a refresh.
    window.__seavantoDebug = {
      log: (msg) => {
        const entry = `[${new Date().toISOString()}] ${msg}`;
        try {
          const prev = JSON.parse(localStorage.getItem("seavanto_debug_log") || "[]");
          const next = [...prev, entry].slice(-MAX_LOGS);
          localStorage.setItem("seavanto_debug_log", JSON.stringify(next));
          setLogs(next);
        } catch {
          setLogs((p) => [...p, entry].slice(-MAX_LOGS));
        }
      },
      clear: () => {
        try { localStorage.removeItem("seavanto_debug_log"); } catch {}
        setLogs([]);
        setErrors([]);
      },
      dump: () => ({
        logs: (() => { try { return JSON.parse(localStorage.getItem("seavanto_debug_log") || "[]"); } catch { return []; } })(),
        errors,
        build: process.env.REACT_APP_BUILD_ID || "dev",
        ua: navigator.userAgent,
      }),
    };

    // Hydrate any breadcrumbs left from before this mount (e.g. a previous
    // page-load that crashed and reloaded).
    try {
      const prev = JSON.parse(localStorage.getItem("seavanto_debug_log") || "[]");
      if (prev.length) setLogs(prev);
    } catch {}

    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
    // We intentionally bind these listeners once on mount; `errors` is
    // referenced inside `dump()` but that helper is invoked imperatively
    // (not on every render), and even if it returns a stale snapshot the
    // diagnostic value isn't affected. Keep effect deps empty on purpose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismiss = (idx) => setErrors((prev) => prev.filter((_, i) => i !== idx));
  const dismissAll = () => setErrors([]);

  if ((user?.email || "").toLowerCase() !== DEBUG_ACCOUNT) return null;

  if (errors.length === 0 && !openLogs) {
    // Render an unobtrusive floating "debug" pill that only shows up when
    // breadcrumbs exist — keeps prod clean for everyone else.
    if (logs.length === 0) return null;
    return (
      <button
        type="button"
        onClick={() => setOpenLogs(true)}
        data-testid="debug-pill"
        style={{
          position: "fixed", bottom: 12, left: 12, zIndex: 99999,
          background: "rgba(0,0,0,0.6)", color: "white", border: "none",
          borderRadius: 999, padding: "6px 10px", fontSize: 11,
          fontFamily: "monospace", cursor: "pointer",
        }}
      >
        debug ({logs.length})
      </button>
    );
  }

  return (
    <div
      data-testid="global-error-overlay"
      style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 99999,
        maxHeight: "60vh", overflow: "auto",
        background: "rgba(220,38,38,0.96)", color: "white",
        fontFamily: "monospace", fontSize: 12, padding: 12,
        boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <strong>Seavanto debug — {errors.length} error(s), {logs.length} log(s)</strong>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            onClick={() => setOpenLogs((v) => !v)}
            data-testid="debug-toggle-logs"
            style={{ background: "rgba(255,255,255,0.2)", color: "white", border: "none", padding: "4px 8px", borderRadius: 4, cursor: "pointer" }}
          >
            {openLogs ? "hide logs" : "show logs"}
          </button>
          <button
            type="button"
            onClick={dismissAll}
            data-testid="debug-dismiss-all"
            style={{ background: "rgba(255,255,255,0.2)", color: "white", border: "none", padding: "4px 8px", borderRadius: 4, cursor: "pointer" }}
          >
            dismiss all
          </button>
        </div>
      </div>
      {errors.map((e, i) => (
        <div key={i} style={{ borderTop: "1px solid rgba(255,255,255,0.2)", paddingTop: 6, marginTop: 6 }}>
          <div style={{ fontWeight: "bold" }}>{e.source}</div>
          <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", margin: 0 }}>{e.msg}</pre>
          <button
            type="button"
            onClick={() => dismiss(i)}
            style={{ marginTop: 4, background: "rgba(255,255,255,0.2)", color: "white", border: "none", padding: "2px 6px", borderRadius: 4, cursor: "pointer", fontSize: 10 }}
          >
            dismiss
          </button>
        </div>
      ))}
      {openLogs && (
        <div style={{ borderTop: "1px solid rgba(255,255,255,0.2)", paddingTop: 6, marginTop: 6 }}>
          <strong>Breadcrumbs</strong>
          <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", margin: 0, fontSize: 11 }}>
            {logs.length ? logs.join("\n") : "(no breadcrumbs yet)"}
          </pre>
        </div>
      )}
      <div style={{ marginTop: 8, fontSize: 10, opacity: 0.85 }}>
        build: <code>{process.env.REACT_APP_BUILD_ID || "dev"}</code> · ua: <code>{navigator.userAgent.slice(0, 80)}</code>
      </div>
    </div>
  );
}
